import type { Track } from "../api.js";
import { Deck, type DeckSnapshot } from "../audio/deck.js";
import type { SegmentRequest } from "../audio/load.js";
import { effectiveBounds } from "../automix/tails.js";
import { fetchBeats, fetchPeaks } from "../waveform/peaks.js";
import { fitToDisplay, renderDetail, renderOverview, THEME } from "../waveform/render.js";
import { duration as formatDuration, escapeHtml } from "./format.js";
import { availableCap, needsRegion } from "./region.js";
import { pickRegion } from "./region-picker.js";

/** Seconds visible across the detail waveform. */
const DETAIL_WINDOW = 8;

export class DeckPanel {
  readonly deck: Deck;
  private snapshot: DeckSnapshot;
  private overviewCache: HTMLCanvasElement | null = null;
  private overviewKey = "";
  private dragging: { pointerId: number; lastX: number } | null = null;

  constructor(
    private readonly root: HTMLElement,
    id: string,
    private readonly requestSelectedTrack: () => Track | null,
    /** What the server will actually serve in one region, from /api/health. */
    private readonly serverMaxSegment: number | null = null,
  ) {
    this.deck = new Deck(id, (s) => {
      this.snapshot = s;
      this.render();
    });
    this.snapshot = this.deck.snapshot();

    this.root.classList.add("deck");
    this.root.dataset["deck"] = id;
    this.root.innerHTML = this.chrome(id);
    this.bind();
    this.render();
  }

  private chrome(id: string): string {
    return `
      <div class="deck-head">
        <span class="deck-id">${escapeHtml(id)}</span>
        <span class="deck-title muted">empty — drag a track here, or press Load</span>
        <span class="spacer"></span>
        <button data-act="load" type="button">Load</button>
        <button data-act="eject" type="button" disabled>Eject</button>
      </div>

      <canvas class="wave-overview" height="56"></canvas>
      <canvas class="wave-detail" height="110"></canvas>

      <div class="deck-controls">
        <button data-act="play" type="button" disabled>Play</button>
        <button data-act="cue" type="button" disabled title="Jump to cue point">Cue</button>
        <button data-act="setcue" type="button" disabled title="Set cue at playhead">Set</button>
        <span class="deck-time num">0:00 / 0:00</span>

        <span class="spacer"></span>

        <div class="nudge">
          <button data-act="nudge-back" type="button" disabled title="Hold to slow down">&#9664;</button>
          <button data-act="nudge-fwd" type="button" disabled title="Hold to speed up">&#9654;</button>
        </div>

        <div class="tempo">
          <div class="tempo-row">
            <span class="deck-bpm num">—</span>
            <button data-act="range" type="button" title="Tempo fader range">&plusmn;8%</button>
            <button data-act="half" type="button" title="Half time">&frac12;</button>
            <button data-act="double" type="button" title="Double time">2&times;</button>
            <button data-act="reset" type="button" title="Reset tempo to 0%">0</button>
          </div>
          <input class="tempo-fader" type="range" min="-100" max="100" step="0.5" value="0" />
          <span class="tempo-pct num muted">0.00%</span>
        </div>
      </div>
      <div class="deck-status"></div>
    `;
  }

  private el<T extends Element = HTMLElement>(selector: string): T {
    const node = this.root.querySelector(selector);
    if (!node) throw new Error(`missing ${selector} in deck`);
    return node as T;
  }

  private act(name: string): HTMLButtonElement {
    return this.el<HTMLButtonElement>(`[data-act="${name}"]`);
  }

  private bind(): void {
    // Any control on this deck counts as the operator taking over.
    this.root.addEventListener("pointerdown", () => this.onManualControl?.(), true);

    this.act("play").addEventListener("click", () => void this.deck.togglePlay());
    this.act("cue").addEventListener("click", () => this.deck.returnToCue());
    this.act("setcue").addEventListener("click", () => this.deck.setCue());
    this.act("eject").addEventListener("click", () => this.deck.unload());
    this.act("load").addEventListener("click", () => {
      const track = this.requestSelectedTrack();
      if (track) void this.load(track);
    });

    this.act("range").addEventListener("click", () => this.deck.cycleTempoRange());
    this.act("reset").addEventListener("click", () => {
      this.el<HTMLInputElement>(".tempo-fader").value = "0";
      this.deck.resetTempo();
    });
    this.act("half").addEventListener("click", () => {
      this.deck.setHalfDouble(this.snapshot.halfDouble === 0.5 ? 1 : 0.5);
    });
    this.act("double").addEventListener("click", () => {
      this.deck.setHalfDouble(this.snapshot.halfDouble === 2 ? 1 : 2);
    });

    this.el<HTMLInputElement>(".tempo-fader").addEventListener("input", (event) => {
      this.deck.setTempoFader(Number((event.target as HTMLInputElement).value) / 100);
    });

    this.bindNudge("nudge-back", -1);
    this.bindNudge("nudge-fwd", 1);

    // Click the overview to seek anywhere in the track.
    const overview = this.el<HTMLCanvasElement>(".wave-overview");
    overview.addEventListener("click", (event) => {
      const { duration } = this.snapshot;
      if (!duration) return;
      const rect = overview.getBoundingClientRect();
      this.deck.seek(((event.clientX - rect.left) / rect.width) * duration);
    });

    this.bindShove();
    this.bindDrop();
  }

  private bindNudge(action: string, direction: 1 | -1): void {
    const button = this.act(action);
    const start = (event: PointerEvent) => {
      button.setPointerCapture(event.pointerId);
      this.deck.startNudge(direction);
    };
    button.addEventListener("pointerdown", start);
    button.addEventListener("pointerup", () => this.deck.endNudge());
    button.addEventListener("pointercancel", () => this.deck.endNudge());
    // Losing the window mid-nudge would otherwise leave the deck running fast.
    button.addEventListener("lostpointercapture", () => this.deck.endNudge());
  }

  /** Drag the detail waveform to shove playback (P6). */
  private bindShove(): void {
    const canvas = this.el<HTMLCanvasElement>(".wave-detail");

    canvas.addEventListener("pointerdown", (event) => {
      if (!this.snapshot.peaksReady) return;
      canvas.setPointerCapture(event.pointerId);
      this.dragging = { pointerId: event.pointerId, lastX: event.clientX };
      canvas.classList.add("shoving");
    });

    canvas.addEventListener("pointermove", (event) => {
      if (!this.dragging || event.pointerId !== this.dragging.pointerId) return;
      const dx = event.clientX - this.dragging.lastX;
      this.dragging.lastX = event.clientX;
      const secondsPerPixel = DETAIL_WINDOW / canvas.clientWidth;
      // Dragging right pulls earlier audio under the playhead, like a platter.
      this.deck.shove(-dx * secondsPerPixel);
    });

    const end = () => {
      this.dragging = null;
      canvas.classList.remove("shoving");
    };
    canvas.addEventListener("pointerup", end);
    canvas.addEventListener("pointercancel", end);
    canvas.addEventListener("lostpointercapture", end);
  }

  private bindDrop(): void {
    this.root.addEventListener("dragover", (event) => {
      if (!event.dataTransfer?.types.includes("application/x-track-id")) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      this.root.classList.add("drop-target");
    });
    this.root.addEventListener("dragleave", (event) => {
      if (event.target === this.root) this.root.classList.remove("drop-target");
    });
    this.root.addEventListener("drop", (event) => {
      const raw = event.dataTransfer?.getData("application/x-track-id");
      this.root.classList.remove("drop-target");
      if (!raw) return;
      event.preventDefault();
      const track = this.pendingDrop?.(Number(raw));
      if (track) void this.load(track);
    });
  }

  /** Resolves a dropped track id against the library's current page. */
  pendingDrop: ((trackId: number) => Track | null) | null = null;

  /** Notified when the operator touches this deck, so auto-mix can yield (X2). */
  onManualControl: (() => void) | null = null;

  /**
   * Load a track, as a manual drag or Load press implies.
   *
   * Every manual route in on both panels funnels through here — the deck's own
   * Load button, its drop target, and the transport bar's buttons. Auto-mix
   * does not: it calls `Deck.load` directly with its own excerpt. That is why
   * the region picker can live here without ever appearing mid-mix.
   *
   * Short tracks load whole, as a person choosing a track means. Long ones ask
   * first, because a whole-track decode here runs to a gigabyte.
   */
  async load(track: Track): Promise<void> {
    this.overviewCache = null;
    this.overviewKey = "";

    const segment = await this.chooseRegion(track);
    if (segment === null) return;
    await this.deck.load(track, segment);
  }

  /**
   * The region of a long track to load, chosen by hand.
   *
   * Three outcomes, and the difference matters: `undefined` is "load the whole
   * thing" for anything short enough, a `SegmentRequest` is a chosen region,
   * and `null` is "load nothing" — the picker was cancelled, or refused.
   *
   * The region comes back in **track time**, which is the frame
   * `SegmentRequest.start` is in, so it is passed straight through.
   */
  private async chooseRegion(track: Track): Promise<SegmentRequest | undefined | null> {
    const duration = track.duration ?? 0;
    if (!needsRegion(duration)) return undefined;

    const status = this.el(".deck-status");

    // No analysis means no peak file, and a picker with nothing to show is
    // worse than a refusal. This is what happens above ANALYSIS_MAX_DURATION,
    // which is exactly where picking a region would help most; the fix is a
    // peaks pass independent of the analysis, and it does not exist yet.
    if (track.analysis_state !== "ok") {
      status.innerHTML =
        `<span class="warn">${escapeHtml(formatDuration(duration))} long and not analysed,` +
        ` so there is no waveform to choose a region from. This one cannot be loaded by hand.</span>`;
      return null;
    }

    status.textContent = "Reading the waveform…";
    let peaks;
    let beats;
    try {
      const controller = new AbortController();
      [peaks, beats] = await Promise.all([
        fetchPeaks(track.id, "overview", controller.signal),
        // Beats only place the snapping; a track without a grid still picks.
        fetchBeats(track.id, controller.signal).catch(() => null),
      ]);
    } catch (error) {
      status.innerHTML = `<span class="warn">Could not read the waveform: ${escapeHtml(String(error))}</span>`;
      return null;
    }
    status.textContent = "";

    const region = await pickRegion({
      track,
      peaks,
      bounds: effectiveBounds(peaks, duration),
      beats: Array.from(beats?.beats ?? []),
      // Never offer more than the server will send, or the dialog would promise
      // a region the deck then does not hold.
      cap: availableCap(this.serverMaxSegment),
    });
    if (!region) return null;
    return { start: region.start, duration: region.length };
  }

  /** Called once per animation frame by the shared render loop. */
  renderFrame(): void {
    const detail = this.el<HTMLCanvasElement>(".wave-detail");
    const overview = this.el<HTMLCanvasElement>(".wave-overview");
    const position = this.deck.position();
    const { duration } = this.snapshot;

    if (fitToDisplay(overview)) this.drawOverview(overview, position, duration);
    if (fitToDisplay(detail) && this.deck.peaksDetail) {
      renderDetail(detail, {
        peaks: this.deck.peaksDetail,
        beats: this.deck.beats,
        position,
        window: DETAIL_WINDOW,
        cue: this.snapshot.cue,
      });
    } else if (fitToDisplay(detail)) {
      const ctx = detail.getContext("2d");
      if (ctx) {
        ctx.fillStyle = THEME.background;
        ctx.fillRect(0, 0, detail.width, detail.height);
      }
    }

    if (this.snapshot.state === "playing") {
      this.el(".deck-time").textContent =
        `${formatDuration(position)} / ${formatDuration(duration)}`;
    }
  }

  private drawOverview(canvas: HTMLCanvasElement, position: number, total: number): void {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const key = `${canvas.width}x${canvas.height}:${this.snapshot.track?.id ?? "none"}`;
    if (this.deck.peaksOverview && this.overviewKey !== key) {
      this.overviewCache = renderOverview(this.deck.peaksOverview, canvas.width, canvas.height);
      this.overviewKey = key;
    }

    ctx.fillStyle = THEME.background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (this.overviewCache) ctx.drawImage(this.overviewCache, 0, 0);

    // Dim what is not in memory, so an excerpt does not look like a track that
    // simply refuses to seek.
    const held = this.snapshot.window;
    if (this.snapshot.excerpt && total > 0) {
      ctx.fillStyle = "rgba(14, 15, 18, 0.66)";
      const from = Math.max(0, (held.offset / total) * canvas.width);
      const to = Math.min(canvas.width, ((held.offset + held.length) / total) * canvas.width);
      ctx.fillRect(0, 0, from, canvas.height);
      ctx.fillRect(to, 0, canvas.width - to, canvas.height);
    }

    if (total > 0) {
      if (this.snapshot.cue !== null) {
        ctx.fillStyle = THEME.cue;
        ctx.fillRect(Math.round((this.snapshot.cue / total) * canvas.width), 0, 2, canvas.height);
      }
      ctx.fillStyle = THEME.playhead;
      ctx.fillRect(Math.round((position / total) * canvas.width), 0, 2, canvas.height);
    }
  }

  private render(): void {
    const s = this.snapshot;
    const loaded = s.state === "ready" || s.state === "playing";

    this.act("play").textContent = s.state === "playing" ? "Pause" : "Play";
    this.act("play").disabled = !loaded;
    this.act("cue").disabled = !loaded;
    this.act("setcue").disabled = !loaded;
    this.act("eject").disabled = s.state === "empty";
    this.root.classList.toggle("playing", s.state === "playing");

    const title = this.el(".deck-title");
    if (s.track) {
      const artist = s.track.artist ? `${escapeHtml(s.track.artist)} — ` : "";
      title.innerHTML = `${artist}${escapeHtml(s.track.title ?? s.track.path)}`;
      title.classList.remove("muted");
    } else {
      title.textContent = "empty — drag a track here, or press Load";
      title.classList.add("muted");
    }

    this.el(".deck-time").textContent =
      `${formatDuration(s.position)} / ${formatDuration(s.duration)}`;

    // The effective BPM is the number you actually match against, so it is the
    // one shown large; where no tempo could be asserted there is nothing to show.
    const bpm = this.el(".deck-bpm");
    if (s.effectiveBpm !== null) {
      bpm.textContent = s.effectiveBpm.toFixed(1);
      bpm.classList.remove("muted");
      bpm.title = `base ${s.baseBpm?.toFixed(1)} BPM`;
    } else {
      bpm.textContent = s.track ? "no tempo" : "—";
      bpm.classList.add("muted");
      bpm.title = s.track ? "no steady tempo detected; set one by hand in the library" : "";
    }

    this.act("range").textContent = `±${Math.round(s.tempoRange * 100)}%`;
    this.act("half").classList.toggle("active", s.halfDouble === 0.5);
    this.act("double").classList.toggle("active", s.halfDouble === 2);

    const percent = s.tempoFader * s.tempoRange * 100;
    this.el(".tempo-pct").textContent = `${percent >= 0 ? "+" : ""}${percent.toFixed(2)}%`;

    for (const action of ["nudge-back", "nudge-fwd"]) {
      this.act(action).disabled = !loaded;
    }
    this.root.classList.toggle("nudging", s.nudging);

    const status = this.el(".deck-status");
    if (s.state === "loading" && s.progress) {
      status.innerHTML = s.progress.decoding
        ? `<span class="working">Decoding…</span>`
        : `<span class="working">Loading ${
            s.progress.downloaded === null ? "…" : `${Math.round(s.progress.downloaded * 100)}%`
          }</span>`;
    } else if (s.state === "error") {
      status.innerHTML = `<span class="bad">${escapeHtml(s.error ?? "load failed")}</span>`;
    } else if (loaded && !s.peaksReady) {
      status.innerHTML = `<span class="muted">No waveform — track not analysed yet.</span>`;
    } else {
      status.textContent = "";
    }
  }
}
